/**
 * Claude API Usage dashboard.
 *
 * Surfaces the in-process budget counters + persistent response cache
 * stats so operators can see today's Claude spend at a glance.
 *
 * Backed by GET /api/v1/admin/claude-usage and
 * POST /api/v1/admin/claude-usage/purge-expired-cache.
 */
import React, { useEffect, useState } from 'react';
import { Gauge, RefreshCw, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';

interface UsageToday {
  date_utc: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
  cache_hits: number;
  cache_misses: number;
  denied: number;
}

interface Limits {
  calls_per_minute: number;
  calls_per_day: number;
  input_tokens_per_day: number;
  output_tokens_per_day: number;
  enabled: boolean;
}

interface Remaining {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  estimated_budget_usd: number;
}

interface CallerStats {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

interface CacheStats {
  total_rows: number;
  expired_rows: number;
  cached_input_tokens?: number;
  cached_output_tokens?: number;
  estimated_savings_usd: number;
}

interface UsageResponse {
  today: UsageToday;
  limits: Limits;
  remaining: Remaining;
  per_caller: Record<string, CallerStats>;
  cache: CacheStats;
}

function fmtInt(n: number | undefined | null): string {
  if (n === undefined || n === null) return '0';
  return Number(n).toLocaleString();
}

function fmtMoney(n: number | undefined | null): string {
  if (n === undefined || n === null) return '$0.00';
  return `$${Number(n).toFixed(2)}`;
}

function progressPercent(used: number, limit: number): number {
  if (!limit || limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

function progressColor(pct: number): string {
  if (pct >= 90) return 'bg-red-500';
  if (pct >= 70) return 'bg-yellow-500';
  return 'bg-blue-500';
}

const ClaudeUsagePage: React.FC = () => {
  const [data, setData] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<UsageResponse>('/admin/claude-usage');
      setData(res.data);
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message || e?.message || 'Failed to load usage'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handlePurge = async () => {
    setPurging(true);
    try {
      const res = await api.post<{ deleted: number }>(
        '/admin/claude-usage/purge-expired-cache'
      );
      toast.success(`Purged ${res.data.deleted} expired cache rows`);
      await load();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message || e?.message || 'Purge failed'
      );
    } finally {
      setPurging(false);
    }
  };

  const today = data?.today;
  const limits = data?.limits;
  const remaining = data?.remaining;
  const cache = data?.cache;
  const callers = data?.per_caller || {};

  const callerRows = Object.entries(callers).sort(
    (a, b) => b[1].cost_usd - a[1].cost_usd
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Gauge className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">
              Claude API Usage
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={load}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            <button
              onClick={handlePurge}
              disabled={purging}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {purging ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Purge expired cache
            </button>
          </div>
        </div>

        {limits && !limits.enabled && (
          <div className="flex items-start bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                Claude budget controls are DISABLED
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                Set <code>CLAUDE_BUDGET_ENABLED=true</code> on the backend to
                enforce per-minute + daily caps.
              </p>
            </div>
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading usage…
          </div>
        ) : today && limits && remaining && cache ? (
          <>
            {/* Headline cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Calls Today
                </p>
                <p className="text-3xl font-semibold text-gray-900 mt-1">
                  {fmtInt(today.calls)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  of {fmtInt(limits.calls_per_day)} daily cap
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Tokens Today
                </p>
                <p className="text-3xl font-semibold text-gray-900 mt-1">
                  {fmtInt(today.input_tokens + today.output_tokens)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {fmtInt(today.input_tokens)} in + {fmtInt(today.output_tokens)}{' '}
                  out
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Cost Today
                </p>
                <p className="text-3xl font-semibold text-gray-900 mt-1">
                  {fmtMoney(today.estimated_cost_usd)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {today.denied > 0 && (
                    <span className="text-red-600">
                      {today.denied} denied by caps
                    </span>
                  )}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Cache Hits
                </p>
                <p className="text-3xl font-semibold text-gray-900 mt-1">
                  {fmtInt(today.cache_hits)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {fmtInt(today.cache_misses)} misses
                </p>
              </div>
            </div>

            {/* Progress bars */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                Daily budget utilization
              </h2>
              <div className="space-y-4">
                {[
                  {
                    label: 'Calls',
                    used: today.calls,
                    cap: limits.calls_per_day,
                  },
                  {
                    label: 'Input tokens',
                    used: today.input_tokens,
                    cap: limits.input_tokens_per_day,
                  },
                  {
                    label: 'Output tokens',
                    used: today.output_tokens,
                    cap: limits.output_tokens_per_day,
                  },
                ].map((row) => {
                  const pct = progressPercent(row.used, row.cap);
                  return (
                    <div key={row.label}>
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>{row.label}</span>
                        <span>
                          {fmtInt(row.used)} / {fmtInt(row.cap)} ({pct}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`${progressColor(pct)} h-2 rounded-full`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Per-minute cap: {fmtInt(limits.calls_per_minute)} calls. Budget
                resets at UTC midnight ({today.date_utc}).
              </p>
            </div>

            {/* Per-caller table */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900">
                  Spend by caller (today)
                </h2>
              </div>
              {callerRows.length === 0 ? (
                <p className="px-4 py-6 text-sm text-gray-500">
                  No Claude calls recorded yet today.
                </p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">
                        Caller
                      </th>
                      <th className="px-4 py-2 text-right font-medium text-gray-600">
                        Calls
                      </th>
                      <th className="px-4 py-2 text-right font-medium text-gray-600">
                        Input tokens
                      </th>
                      <th className="px-4 py-2 text-right font-medium text-gray-600">
                        Output tokens
                      </th>
                      <th className="px-4 py-2 text-right font-medium text-gray-600">
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {callerRows.map(([name, s]) => (
                      <tr key={name}>
                        <td className="px-4 py-2 text-gray-900 font-mono text-xs">
                          {name}
                        </td>
                        <td className="px-4 py-2 text-right">{fmtInt(s.calls)}</td>
                        <td className="px-4 py-2 text-right">
                          {fmtInt(s.input_tokens)}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {fmtInt(s.output_tokens)}
                        </td>
                        <td className="px-4 py-2 text-right font-semibold">
                          {fmtMoney(s.cost_usd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Cache stats */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                Persistent response cache
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs uppercase text-gray-500">Rows</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">
                    {fmtInt(cache.total_rows)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Expired</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">
                    {fmtInt(cache.expired_rows)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">
                    Cached input tokens
                  </p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">
                    {fmtInt(cache.cached_input_tokens || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">
                    Lifetime savings est.
                  </p>
                  <p className="text-xl font-semibold text-green-600 mt-1">
                    {fmtMoney(cache.estimated_savings_usd)}
                  </p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500">No usage data.</p>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ClaudeUsagePage;
